import { clsx, type ClassValue } from "clsx";
import { composeRenderProps } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv } from "tailwind-variants";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const focusRing = tv({
  base: "outline outline-border-mode forced-colors:outline-[Highlight] outline-offset-2",
  variants: {
    isFocusVisible: {
      false: "outline-0",
      true: "outline-2",
    },
  },
});

export function composeTailwindRenderProps<T>(
  className: string | ((v: T) => string) | undefined,
  tw: string
): string | ((v: T) => string) {
  return composeRenderProps(className, (className) => twMerge(tw, className));
}

/**
 * Returns black (#000000) or white (#ffffff) for optimal contrast against the given background color.
 * Uses relative luminance (WCAG formula) - threshold 0.5.
 */
export function getContrastTextColor(backgroundColor: string): string {
  let r = 0,
    g = 0,
    b = 0;

  // Hex: #fff or #ffffff
  const hexMatch = backgroundColor.match(/^#?([a-f\d])([a-f\d])([a-f\d])$/i);
  if (hexMatch) {
    r = parseInt(hexMatch[1] + hexMatch[1], 16) / 255;
    g = parseInt(hexMatch[2] + hexMatch[2], 16) / 255;
    b = parseInt(hexMatch[3] + hexMatch[3], 16) / 255;
  } else {
    const hex6Match = backgroundColor.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (hex6Match) {
      r = parseInt(hex6Match[1], 16) / 255;
      g = parseInt(hex6Match[2], 16) / 255;
      b = parseInt(hex6Match[3], 16) / 255;
    } else {
      const rgbMatch = backgroundColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (rgbMatch) {
        r = parseInt(rgbMatch[1], 10) / 255;
        g = parseInt(rgbMatch[2], 10) / 255;
        b = parseInt(rgbMatch[3], 10) / 255;
      } else {
        return "#000000"; // fallback for named colors etc.
      }
    }
  }

  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 0.5 ? "#000000" : "#ffffff";
}

export function formatCurrency(
  amount: number,
  leading: number = 2,
  rounding: "floor" | "ceil" = "floor"
) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: leading,
    maximumFractionDigits: 2,
  })?.format(Math[rounding](amount));
}

// https://stackoverflow.com/a/9462382
export function nFormatter(num: number, digits: number = 2) {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  const item = lookup
    .slice()
    .reverse()
    .find((item) => num >= item.value);
  return item
    ? (num / item.value).toFixed(digits).replace(regexp, "").concat(item.symbol)
    : "0";
}
